import { AsyncPipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { NamedQuery, RuntimeParameter } from '@ddv/models';
import { isAdditionalFilterNamesMatch } from '@ddv/named-queries';
import { DropdownOption } from '@hs/ui-core-common';
import { MultiSelectDropdownComponent } from '@hs/ui-core-dropdowns';
import { Observable, of, shareReplay, take } from 'rxjs';

import { AdditionalFilter } from '../../models/additional-filter';
import { AdditionalFiltersService } from '../../services/additional-filters.service';

@Component({
    selector: 'ddv-additional-filters',
    templateUrl: './additional-filters.component.html',
    styleUrls: ['./additional-filters.component.scss'],
    imports: [
        AsyncPipe,
        MultiSelectDropdownComponent,
    ],
})
export class AdditionalFiltersComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() clientCode = '';
    @Input() id: number | undefined;
    @Input() additionalFilters: Record<string, DropdownOption[]>[] = [];
    @Input() namedQueries: NamedQuery[] = [];

    @Output() additionalFiltersChange = new EventEmitter<Record<string, DropdownOption[]>[] | undefined>();

    private readonly additionalFiltersService = inject(AdditionalFiltersService);
    private readonly cdr = inject(ChangeDetectorRef);

    protected filters: AdditionalFilter[] = [];

    @ViewChildren(MultiSelectDropdownComponent)
    private readonly dropdowns: QueryList<MultiSelectDropdownComponent> | undefined;

    private dropdownToOpenIndex = 0;
    private isAdditionalFilterNamesMatch = true;

    ngOnInit(): void {
        this.setFilters();
    }

    ngAfterViewInit(): void {
        this.openNextPendingDropdown();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.id && !changes.id.firstChange) {
            this.setFilters();
            this.dropdownToOpenIndex = 0;
            this.openNextPendingDropdown();
        }
    }

    protected onSelectedOptionsChanged(selectedOptions: DropdownOption[], filter: AdditionalFilter): void {
        this.cdr.detectChanges();
        filter.error = !selectedOptions.length ? 'Please select at least one option' : undefined;
        this.updateAdditionalFilters(selectedOptions, filter.name);
        this.additionalFiltersChange.emit(!this.additionalFilters.length ? undefined : this.additionalFilters);

        if (this.filters[this.dropdownToOpenIndex]?.name === filter.name) {
            this.dropdownToOpenIndex += 1;
            this.openNextPendingDropdown();
        }
    }

    private setFilters(): void {
        this.isAdditionalFilterNamesMatch = isAdditionalFilterNamesMatch(this.namedQueries);
        this.filters = this.namedQueries.reduce((filters: AdditionalFilter[], namedQuery) => {
            const additionalFilters = namedQuery.parameters?.filter((p) => p.type === 'additional-filter') ?? [];
            if (!additionalFilters.length) {
                return filters;
            }

            const uniqueAdditionalFilters = additionalFilters.filter((param) => !filters.some((f) => f.name === param.name));
            if (!uniqueAdditionalFilters.length) {
                return filters;
            }

            return [
                ...filters,
                ...uniqueAdditionalFilters.map((param) => this.mapFilter(param, namedQuery)),
            ];
        }, []);
    }

    private mapFilter(param: RuntimeParameter, namedQuery: NamedQuery): AdditionalFilter {
        const displayName = param.displayName;
        const name = param.name;
        const queryType = namedQuery.type.name.toLowerCase();
        const options = this.getAdditionalFilterOptions(queryType, name);
        const selectedOptions = this.additionalFilters?.find((p) => Object.keys(p)[0] === name)?.[name] ?? [];
        const error = !selectedOptions.length ? 'Please select at least one option' : undefined;
        return { displayName, name, options, selectedOptions, error };
    }

    private getAdditionalFilterOptions(queryType: string, name: string): Observable<DropdownOption[]> {
        return this.isAdditionalFilterNamesMatch ?
            this.additionalFiltersService
                .getAdditionalFilterOptions(this.clientCode, queryType, name)
                .pipe(shareReplay({ bufferSize: 1, refCount: true })) :
            of([]);
    }

    private openNextPendingDropdown(): void {
        const filter = this.filters[this.dropdownToOpenIndex];

        if (!filter) {
            return;
        }

        if (filter.selectedOptions.length) {
            this.dropdownToOpenIndex += 1;
            this.openNextPendingDropdown();
            return;
        }

        filter.options.pipe(take(1)).subscribe((options) => {
            const dropdown = this.dropdowns?.get(this.dropdownToOpenIndex);
            if (dropdown && !!options.length) {
                dropdown.openList();
            }
        });
    }

    private updateAdditionalFilters(selectedOptions: DropdownOption[], name: string): void {
        if (selectedOptions.length > 0) {
            this.additionalFilters = [
                ...this.additionalFilters.filter((param) => Object.keys(param)[0] !== name),
                { [name]: selectedOptions },
            ];
        } else {
            this.additionalFilters = this.additionalFilters.filter((param) => Object.keys(param)[0] !== name);
        }
    }
}
